import React, { useState } from 'react';
import { useUser, useAuth, useClerk } from '@clerk/clerk-react';
import { useCredits } from '../hooks/useCredits';
import { useApiKeys } from '../hooks/useApiKeys';
import { API_BASE_URL } from '../config/api';

const SettingsPage: React.FC = () => {
  const { user } = useUser();
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const { credits, costPerAnalysis, isLoading } = useCredits();
  const { keys, isLoading: keysLoading, createKey, revokeKey } = useApiKeys();

  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const email = user?.emailAddresses?.[0]?.emailAddress ?? 'N/A';

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setIsCreating(true);
    const rawKey = await createKey(newKeyName.trim());
    if (rawKey) {
      setCreatedKey(rawKey);
      setNewKeyName('');
    }
    setIsCreating(false);
  };

  const handleCopy = async () => {
    if (!createdKey) return;
    await navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (keyId: string) => {
    await revokeKey(keyId);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    try {
      const token = await getToken();
      await fetch(`${API_BASE_URL}/api/user`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await signOut();
    } catch (err) {
      setIsDeletingAccount(false);
      setShowDeleteConfirm(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  };

  return (
    <div className="settings-page">
      <h2 className="settings-title">Settings</h2>

      <div className="settings-section">
        <h3 className="settings-section-title">Account</h3>
        <div className="settings-card">
          <div className="settings-row">
            <span className="settings-label">Email</span>
            <span className="settings-value">{email}</span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Analysis Credits</span>
            <span className={`settings-value${credits !== null && credits < costPerAnalysis ? ' settings-value-low' : ''}`}>
              {isLoading ? '...' : credits ?? '...'}
            </span>
          </div>
          <div className="settings-row">
            <span className="settings-label">Cost per Analysis</span>
            <span className="settings-value">{isLoading ? '...' : `${costPerAnalysis} credit${costPerAnalysis !== 1 ? 's' : ''}`}</span>
          </div>
        </div>
      </div>

      <div className="settings-section">
        <button className="settings-purchase-btn" disabled>
          Purchase Credits — Coming Soon
        </button>
      </div>

      <div className="settings-section">
        <h3 className="settings-section-title">API Keys</h3>
        <p className="settings-section-desc">
          Use API keys to access your telemetry data programmatically.
        </p>

        {/* New key created banner */}
        {createdKey && (
          <div className="apikey-created-banner">
            <span className="apikey-created-label">Copy your key now — it won't be shown again</span>
            <div className="apikey-created-row">
              <code className="apikey-created-value">{createdKey}</code>
              <button className="apikey-copy-btn" onClick={handleCopy}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <button className="apikey-dismiss-btn" onClick={() => setCreatedKey(null)}>
              Done
            </button>
          </div>
        )}

        {/* Create new key */}
        <div className="settings-card" style={{ marginBottom: '0.75rem' }}>
          <div className="apikey-create-row">
            <input
              type="text"
              className="apikey-name-input"
              placeholder="Key name (e.g. My Python Script)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              className="apikey-create-btn"
              onClick={handleCreate}
              disabled={!newKeyName.trim() || isCreating}
            >
              {isCreating ? '...' : 'Create'}
            </button>
          </div>
        </div>

        {/* Key list */}
        <div className="settings-card">
          {keysLoading ? (
            <div className="settings-row">
              <span className="settings-label">Loading...</span>
            </div>
          ) : keys.length === 0 ? (
            <div className="settings-row">
              <span className="settings-label">No API keys yet</span>
            </div>
          ) : (
            keys.map((key) => (
              <div className="settings-row apikey-row" key={key.id}>
                <div className="apikey-info">
                  <span className="apikey-name">{key.name}</span>
                  <span className="apikey-meta">
                    {key.key_prefix}... · Created {formatDate(key.created_at)}
                    {key.last_used_at && ` · Last used ${formatDate(key.last_used_at)}`}
                  </span>
                </div>
                <button className="apikey-revoke-btn" onClick={() => handleRevoke(key.id)}>
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
      </div>
      <div className="settings-section">
        <button
          className="settings-delete-btn"
          onClick={() => setShowDeleteConfirm(true)}
        >
          Delete Account
        </button>
      </div>

      {showDeleteConfirm && (
        <div className="settings-delete-overlay" onClick={() => !isDeletingAccount && setShowDeleteConfirm(false)}>
          <div className="settings-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="settings-delete-modal-title">Delete Account</h3>
            <p className="settings-delete-warning">
              This will permanently delete your account and all associated data. This cannot be undone.
            </p>
            <div className="settings-delete-actions">
              <button
                className="settings-delete-confirm-btn"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? 'Deleting...' : 'Confirm Delete'}
              </button>
              <button
                className="settings-delete-cancel-btn"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingAccount}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
